import { ConnXClient as GraphQLClient } from "../apolloClient";
import gql from "graphql-tag";

export interface RVModelOrder {
  groupID: string;
  docID: string;
  name: string;
  desc: string;
  revision: number;
  serialNumber: string;
  orderDate: number;
  provisioned: boolean;
  iotThingName?: string;
  // The user group the order is assigned to: the group of its RV model, or
  // one of that group's child groups.
  userGroupID?: string;
  // Hidden from the Orders page and the Models and orders dashboard, e.g. an
  // order whose device no longer reports. Set by a model/order admin.
  hidden: boolean;
}

interface CreateRVModelOrderInput {
  groupID: string;
  name: string;
  desc: string;
  serialNumber: string;
}

interface ListRVOrdersInput {
  filterName?: string;
  nextKey?: string;
}

interface ListRVOrdersOutput {
  models: RVModelOrder[];
  nextKey?: string;
}

interface UpdateRVModelOrderInput {
  name?: string;
  desc?: string;
}

const fragments = {
  rvmodelorder: gql`
    fragment rvmodelorder on RVModelOrder {
      groupID
      docID
      name
      desc
      revision
      serialNumber
      orderDate
      provisioned
      iotThingName
      userGroupID
      hidden
    }
  `,
};

class RVModelOrderAPI {
  async get(modelID: string, orderID: string): Promise<RVModelOrder> {
    const GQL = gql`
      query($groupID: ID!, $docID: ID!) {
        getRVModelOrder(groupID: $groupID, docID: $docID) {
          ...rvmodelorder
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      getRVModelOrder: RVModelOrder;
    }
    const r = await GraphQLClient.query<Output>({
      query: GQL,
      variables: { groupID: modelID, docID: orderID },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.getRVModelOrder;
  }

  async update(
    order: RVModelOrder,
    changes: UpdateRVModelOrderInput
  ): Promise<RVModelOrder> {
    const GQL = gql`
      mutation(
        $groupID: ID!
        $docID: ID!
        $revision: Int!
        $input: UpdateRVModelOrderInput!
      ) {
        updateRVModelOrder(
          groupID: $groupID
          docID: $docID
          revision: $revision
          input: $input
        ) {
          ...rvmodelorder
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      updateRVModelOrder: RVModelOrder;
    }
    const r = await GraphQLClient.mutate<Output>({
      mutation: GQL,
      variables: {
        groupID: order.groupID,
        docID: order.docID,
        revision: order.revision,
        input: {
          name: changes.name,
          desc: changes.desc,
        },
      },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.updateRVModelOrder;
  }

  // Assigns the order to a user group: the group of its RV model, or one of
  // that group's child groups.
  async setUserGroup(
    modelID: string,
    orderID: string,
    userGroupID: string
  ): Promise<RVModelOrder> {
    const GQL = gql`
      mutation($groupID: ID!, $docID: ID!, $userGroupID: ID!) {
        setRVOrderUserGroup(
          groupID: $groupID
          docID: $docID
          userGroupID: $userGroupID
        ) {
          ...rvmodelorder
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      setRVOrderUserGroup: RVModelOrder;
    }
    const r = await GraphQLClient.mutate<Output>({
      mutation: GQL,
      variables: { groupID: modelID, docID: orderID, userGroupID },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.setRVOrderUserGroup;
  }

  // Hides the order from the Orders page and the Models and orders dashboard,
  // or shows it again. Model/order admins only.
  async setHidden(
    modelID: string,
    orderID: string,
    hidden: boolean
  ): Promise<RVModelOrder> {
    const GQL = gql`
      mutation($groupID: ID!, $docID: ID!, $hidden: Boolean!) {
        setRVOrderHidden(groupID: $groupID, docID: $docID, hidden: $hidden) {
          ...rvmodelorder
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      setRVOrderHidden: RVModelOrder;
    }
    const r = await GraphQLClient.mutate<Output>({
      mutation: GQL,
      variables: { groupID: modelID, docID: orderID, hidden },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.setRVOrderHidden;
  }

  async delete(modelID: string, orderID: string): Promise<boolean> {
    const GQL = gql`
      mutation($groupID: ID!, $docID: ID!) {
        deleteRVModelOrder(groupID: $groupID, docID: $docID)
      }
    `;
    interface Output {
      deleteRVModelOrder: boolean;
    }
    const r = await GraphQLClient.mutate<Output>({
      mutation: GQL,
      variables: { groupID: modelID, docID: orderID },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.deleteRVModelOrder;
  }

  async createRVOrder(input: CreateRVModelOrderInput): Promise<RVModelOrder> {
    const GQL = gql`
      mutation($input: CreateRVModelOrderInput!) {
        createRVModelOrder(input: $input) {
          ...rvmodelorder
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      createRVModelOrder: RVModelOrder;
    }
    const r = await GraphQLClient.mutate<Output>({
      mutation: GQL,
      variables: { input: input },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.createRVModelOrder;
  }

  async listRVOrders(
    modelID: string,
    params?: ListRVOrdersInput
  ): Promise<ListRVOrdersOutput> {
    const GQL = gql`
      query($groupID: ID!, $params: ListRVModelOrdersParams) {
        listRVModelOrders(groupID: $groupID, params: $params) {
          models {
            ...rvmodelorder
          }
          nextKey
        }
      }
      ${fragments.rvmodelorder}
    `;
    interface Output {
      listRVModelOrders: ListRVOrdersOutput;
    }
    const r = await GraphQLClient.query<Output>({
      query: GQL,
      variables: {
        groupID: modelID,
        params: params,
      },
    });
    if (!r.data) {
      throw new Error("unexpected result");
    }
    return r.data.listRVModelOrders;
  }
}

export default RVModelOrderAPI;
